import { useMemo, useState } from "react";
import {
  RegisterOptions,
  SubmitHandler,
  useForm,
  useFormState,
  useWatch,
} from "react-hook-form";

import {
  Field,
  FieldHelper,
  FieldLabel,
  FormCard,
  FormPanel,
  LabelRow,
  ToneSegments,
} from "../../../../components/FormPanel";
import { FormMoneyInput, InputDate } from "../../../../components/Input";
import { buildDraftDetails } from "../../../../components/PopUp/confirmShared";
import { localToday } from "../../../../utils";
import {
  tradeDateFloor,
  validateAmount,
  validateTradeDate,
} from "../tradeValidation";
import { ChargeField } from "../ChargeField";
import { InterestDirectionField } from "../InterestDirectionField";
import { NoteField } from "../NoteField";
import { settlementNoteOf } from "../settlementNote";
import { InterestDirection } from "../types";
import { MobileAccountCashForm } from "./MobileAccountCashForm";
import {
  accountCashNetCentsOf,
  ACCOUNT_CASH_COPY,
  ACTION_SEGMENTS,
  createDefaultAccountCash,
  FORM_ID,
  showsInterestTax,
} from "./constants";
import {
  AccountCashAction,
  AccountCashFormValues,
  AccountCashPanelProps,
} from "./types";

// Account fee/interest/adjustment: a sibling of HoldingTradePanel, not three more of its actions
// — those require a position, which these three forbid at the server, so there's none to select.
export const AccountCashPanel = ({
  isOpen,
  onClose,
  investmentName,
  investmentStartDate,
  settlementAccountName,
  handleOnSubmit,
}: AccountCashPanelProps) => {
  const [panelError, setPanelError] = useState<string | null>(null);

  const form = useForm<AccountCashFormValues>({
    defaultValues: createDefaultAccountCash("accountFee"),
    mode: "onBlur",
    reValidateMode: "onChange",
  });
  const { control, getValues, setValue, setError } = form;

  const { isSubmitting, errors } = useFormState({ control });
  const action = useWatch({ control, name: "action" });
  const direction = useWatch({ control, name: "direction" });
  const amountEur = useWatch({ control, name: "amountEur" });

  const copy = ACCOUNT_CASH_COPY[action];
  const directionGroupLabel = copy.directionGroupLabel;
  const showsTax = showsInterestTax({ action, direction });
  const settlementNote = settlementNoteOf({
    accountName: settlementAccountName,
    netCents: accountCashNetCentsOf({ action, amountEur, direction }),
  });

  const minDate = useMemo(
    () => tradeDateFloor(investmentStartDate),
    [investmentStartDate],
  );

  const handleActionChange = (next: AccountCashAction) => {
    setValue("action", next, { shouldDirty: true });
    setPanelError(null);
  };

  const handleDirectionChange = (next: InterestDirection) => {
    setValue("direction", next, { shouldDirty: true });
  };

  const onSubmit: SubmitHandler<AccountCashFormValues> = (data) => {
    setPanelError(null);
    return handleOnSubmit(data, { setError, setPanelError });
  };

  const dateRules: RegisterOptions<AccountCashFormValues, "date"> = {
    required: "Date is required!",
    validate: (value: Date) => validateTradeDate(value, minDate),
  };

  const buildDiscardDetails = () => {
    const values = getValues();
    return buildDraftDetails({
      noun: "entry",
      fieldValues: [values.amountEur, values.taxWithheld, values.note],
    });
  };

  return (
    <FormPanel
      open={isOpen}
      onClose={onClose}
      error={panelError}
      ariaLabel={copy.ariaLabel}
      eyebrow={copy.eyebrow}
      title={copy.title(investmentName)}
      form={form}
      formId={FORM_ID}
      onSubmit={onSubmit}
      submitLabel={copy.submitLabel}
      footerNote={settlementNote}
      discard={{
        title: copy.discardTitle,
        confirmLabel: "Discard",
        details: buildDiscardDetails,
      }}
      mobile={{
        title: copy.compactTitle,
        submitLabel: copy.compactActionLabel,
        fields: (
          <MobileAccountCashForm
            control={control}
            action={action}
            copy={copy}
            minDate={minDate}
            dateRules={dateRules}
            direction={direction}
            directionError={errors.direction?.message}
            showsTax={showsTax}
            isSubmitting={isSubmitting}
            onActionChange={handleActionChange}
            onDirectionChange={handleDirectionChange}
          />
        ),
      }}
    >
      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel>Entry type *</FieldLabel>
          </LabelRow>
          <ToneSegments
            groupLabel="Entry type"
            options={ACTION_SEGMENTS}
            selectedValue={action}
            disabled={isSubmitting}
            onChange={(segment) => handleActionChange(segment.value)}
          />
          <FieldHelper>
            All three apply to the whole account — none names a position.
          </FieldHelper>
        </Field>

        {directionGroupLabel && (
          <InterestDirectionField
            value={direction}
            groupLabel={directionGroupLabel}
            isSubmitting={isSubmitting}
            error={errors.direction?.message}
            onChange={handleDirectionChange}
          />
        )}
      </FormCard>

      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel htmlFor="account-cash-amount">Amount *</FieldLabel>
          </LabelRow>
          <FormMoneyInput
            id="account-cash-amount"
            required
            name="amountEur"
            control={control}
            rules={{ validate: validateAmount }}
            disabled={isSubmitting}
            placeholder="0.00"
          />
          <FieldHelper>{copy.amountHelper}</FieldHelper>
        </Field>

        {showsTax && (
          <ChargeField
            id="account-cash-tax"
            control={control}
            charge="withholdingTax"
            helper={copy.taxHelper ?? ""}
            isSubmitting={isSubmitting}
            taxedIncome="interest"
          />
        )}
      </FormCard>

      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel htmlFor="account-cash-date">Date *</FieldLabel>
          </LabelRow>
          <InputDate
            id="account-cash-date"
            name="date"
            control={control}
            rules={dateRules}
            disabled={isSubmitting}
            minDate={minDate}
            maxDate={localToday()}
            placeholder="YYYY/MM/DD"
          />
          <FieldHelper>
            Must be on or after the account's start date.
          </FieldHelper>
        </Field>

        <NoteField
          id="account-cash-note"
          control={control}
          isSubmitting={isSubmitting}
        />
      </FormCard>
    </FormPanel>
  );
};
